// Configuration file for Richuel application

const isDev = process.env.NODE_ENV !== 'production';

const getApiBaseUrl = () => {
  // Optional build-time override (Create React App)
  if (process.env.REACT_APP_API_BASE_URL) return process.env.REACT_APP_API_BASE_URL;

  if (isDev) return 'http://127.0.0.1:8080';

  // Production: select API by hostname (UAT vs prod hosting)
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'uat.richuel.com') return 'https://api-uat.richuel.com';
  }

  return 'https://api.richuel.com';
};

const apiBaseUrl = getApiBaseUrl();

export const categoryNameFromItem = (item) => {
  if (typeof item === 'string') return item.trim();
  return String(item?.name ?? item?.category ?? '').trim();
};

const config = {
  // API Configuration
  API_BASE_URL: apiBaseUrl,

  CATEGORIES_PATH: '/uploads/categories',
  CONTENTS_PATH: '/uploads/contents',
  CFP_SESSIONS_PATH: '/uploads/cfp_sessions',
  MAX_CFP_SESSION_DOCUMENT_SIZE_MB: 10,

  // API Endpoints
  API_ENDPOINTS: {
    LOGIN: '/api/login',
    SIGNUP: '/api/signup',
    VERIFY_ACCOUNT: '/api/verify-account',
    RESEND_VERIFICATION: '/api/resend-verification',
    FORGOT_PASSWORD: '/api/forgot-password',
    VERIFY_FORGOT_PASSWORD: '/api/verify-forgot-password',
    RESET_PASSWORD: '/api/reset-password',

    // Social Login Endpoints
    GOOGLE_LOGIN: '/api/login/google',
    // Onboarding Endpoints
    ONBOARDING_AI_CHAT: '/api/onboarding-ai/gemini-chat',
    ONBOARDING_AI_UPDATE_CHECKUP_AND_ACTION_PLAN: '/api/onboarding-ai/update-checkup-and-action-plan',

    ONBOARDING_AI_SIMULATION: '/api/v1/simulation/run',
    ONBOARDING_AI_CHART_GENERATOR: '/api/v1/charts/generate',

    // V2 Onboarding Endpoints
    V2_ONBOARDING_AI: '/api/v2/onboarding-ai',
    V2_ONBOARDING_AI_SCENARIO_MAP: '/api/v2/onboarding-ai/scenario-map',
    V2_ONBOARDING_AI_UPDATE: '/api/v2/onboarding-ai/update',
    V2_ONBOARDING_AI_UPDATE_RESULT: '/api/v2/onboarding-ai/update-result',
    V2_ONBOARDING_AI_PATCH_METRICS: '/api/v2/onboarding-ai/patch-metrics',
    V2_ONBOARDING_AI_PROTO3_START: '/api/v2/onboarding-ai/proto3-start',
    V2_ONBOARDING_AI_PROTO3_TURN: '/api/v2/onboarding-ai/proto3-turn',

    // User Endpoints
    USER_UPDATE_NAME: '/api/user/update-name',
    USER_GET_SETTINGS: '/api/user/settings',
    USER_UPDATE_PROFILE: '/api/user/update-profile',
    USER_UPDATE_PHONE: '/api/user/update-phone',
    USER_CHANGE_PASSWORD: '/api/user/change-password',
    USER_DEACTIVATE_ACCOUNT: '/api/user/deactivate-account',
    USER_UPDATE_DELETED_REASON: '/api/user/deleted-reason',
    GOOGLE_USER_EXISTS: '/api/login/google/exists',
    USER_UPDATE_CHAT: '/api/user/update-chat',
    USER_HIDE_CONFETTI: '/api/user/hide-onboarding-confetti',
    USER_WALK_ME_THROUGH_DONE: '/api/user/walk-me-through-done',
    USER_GET_EXPERTS: '/api/user/experts',
    CONTENT_CATALOG: '/api/content',

    USER_INSERT_CFP_SESSION: '/api/user/insert-cfp-session',
    USER_CFP_SESSION_IS_EXISTING: '/api/user/cfp-session-is-existing',
    USER_GET_CFP_SESSIONS: '/api/user/cfp-sessions',
    USER_DELETE_CFP_SESSION: '/api/user/delete-cfp-session',
    USER_EDIT_CFP_SESSION: '/api/user/edit-cfp-session',
    USER_CREATE_CFP_SESSION_CHECKOUT: '/api/user/create-cfp-session-checkout',
    USER_PAY_CFP_SESSION_CHECKOUT: '/api/user/pay-cfp-session-checkout',
    USER_GET_CFP_SESSION_DOCUMENTS: '/api/user/cfp-session-documents',
    USER_CREATE_CFP_SESSION_DOCUMENT: '/api/user/cfp-session-document/create',
    USER_UPDATE_CFP_SESSION_DOCUMENT: '/api/user/cfp-session-document/update',
    USER_DELETE_CFP_SESSION_DOCUMENT: '/api/user/cfp-session-document/delete',

    USER_GET_QUICK_RICHUELS_AND_CHECK_IN_HISTORY: '/api/user/get-quick-richuels-and-check-in-history',

    USER_GET_CHAT: '/api/user/chat',

    USER_GET_CATEGORIES: '/api/user/categories',
    // query: category_id?, content_id?, exclude_not_started? (omit category_id = every active content)
    USER_GET_CONTENTS: '/api/user/contents',
    USER_WATCH_CONTENT: '/api/user/content/watch',
    USER_CHECK_CONTENT_ACTION: '/api/user/content/action/check',
    USER_UNCHECK_CONTENT_ACTION: '/api/user/content/action/uncheck',
    USER_SAVE_CONTENT_REFLECTION: '/api/user/content/reflection',
    USER_UPDATE_PREFERENCES: '/api/user/preferences',

    // Admin Endpoints
    ADMIN_LOGIN: '/api/admin/login',
    ADMIN_DELETE_USER: '/api/admin/user/delete',
    ADMIN_CHAT: '/api/admin/gemini-chat',
    ADMIN_ONBOARDING_AI: '/api/admin/onboarding-ai',
    ADMIN_ONBOARDING_AI_USERS: '/api/admin/onboarding-ai/users',
    ADMIN_ONBOARDING_AI_UPDATE_RESULT: '/api/admin/onboarding-ai/update-result',
    ADMIN_ONBOARDING_AI_UPDATE_CHECKUP_AND_ACTION_PLAN: '/api/admin/onboarding-ai/update-checkup-and-action-plan',
    ADMIN_ONBOARDING_AI_PATCH_METRICS: '/api/admin/onboarding-ai/patch-metrics',

    ADMIN_GET_CATEGORIES: '/api/admin/categories',
    // form: token, cover_image, name
    ADMIN_CREATE_CATEGORY: '/api/admin/category/create',
    // form: token, id, cover_image? (optional), name
    ADMIN_UPDATE_CATEGORY: '/api/admin/category/update',
    ADMIN_DELETE_CATEGORY: '/api/admin/category/delete',

    // query: category_id
    ADMIN_GET_CONTENTS: '/api/admin/contents',
    // form: token, category_id, title, video, cover_image? (optional), description?, actions?
    ADMIN_CREATE_CONTENT: '/api/admin/content/create',
    // form: token, id, category_id, title, cover_image? (optional), remove_cover? (1 clears an existing cover), video?, description?, actions?
    ADMIN_UPDATE_CONTENT: '/api/admin/content/update',
    ADMIN_DELETE_CONTENT: '/api/admin/content/delete',

    ADMIN_GET_USER_CFP_SESSIONS: '/api/admin/user-cfp-sessions',
    ADMIN_GET_CFP_SESSION_DOCUMENTS: '/api/admin/cfp-session-documents',
    ADMIN_MARK_CFP_SESSION_DONE: '/api/admin/cfp-session/done',
  },

  // Local Storage Keys
  STORAGE_KEYS: {
    TOKEN: 'token',
    ACCOUNT_DEACTIVATED_TOKEN: 'accountDeactivatedToken',
    NAME: 'name',
    EMAIL: 'email',
    EXTENDED_WELCOME_BLOCKER: 'extendedWelcomeBlocker',
    PENDING_GOOGLE_SIGNUP: 'pendingGoogleSignup',
    SIGNUP_ONBOARDING_FLOW: 'signupOnboardingFlow',
    SIGNUP_COLLECTED_NAME: 'signupCollectedName',
    ADMIN_ROLE: 'adminRole',
    ONBOARDING_AI_CHAT: 'onboardingAIChat',
    RESEND_COUNTDOWN: 'resendCountdown',
    RESEND_TIMESTAMP: 'resendTimestamp',
    FORGOT_PASSWORD_COUNTDOWN: 'forgotPasswordCountdown',
    FORGOT_PASSWORD_TIMESTAMP: 'forgotPasswordTimestamp',
  },

  // Social Login Configuration
  SOCIAL_LOGIN: {
    GOOGLE_CLIENT_ID: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
  },

  // Logging: used by logger (debug disabled in production)
  LOGGING: {
    DEBUG_ENABLED: isDev,
    CONSOLE_ENABLED: isDev,
  },

  // PostHog Analytics
  POSTHOG: {
    KEY: process.env.REACT_APP_POSTHOG_KEY || '',
    HOST: process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
  },
};

export const buildCategoryCoverUrl = ({ categoryId, filename }) => {
  const filePath = String(filename ?? '').trim();
  if (/^https?:\/\//i.test(filePath)) return filePath;
  if (filePath.startsWith('/')) return `${config.API_BASE_URL}${filePath}`;

  const relativePath = filePath || (String(categoryId ?? '').trim() ? `${String(categoryId).trim()}.png` : '');
  if (!relativePath) return null;

  const encodedPath = relativePath
    .split('/')
    .filter(Boolean)
    .map(encodeURIComponent)
    .join('/');

  return `${config.API_BASE_URL}${config.CATEGORIES_PATH}/${encodedPath}`;
};

export const buildContentVideoUrl = ({ filename }) => {
  const filePath = String(filename ?? '').trim();
  if (!filePath) return null;
  if (/^https?:\/\//i.test(filePath)) return filePath;
  if (filePath.startsWith('/')) return `${config.API_BASE_URL}${filePath}`;

  const encodedPath = filePath
    .split('/')
    .filter(Boolean)
    .map(encodeURIComponent)
    .join('/');

  return `${config.API_BASE_URL}${config.CONTENTS_PATH}/${encodedPath}`;
};

export const buildCfpSessionDocumentUrl = (documentPath) => {
  const filePath = String(documentPath ?? '').trim();
  if (!filePath) return null;
  if (/^https?:\/\//i.test(filePath)) return filePath;
  if (filePath.startsWith('/')) return `${config.API_BASE_URL}${filePath}`;

  const encodedPath = filePath
    .split('/')
    .filter(Boolean)
    .map(encodeURIComponent)
    .join('/');

  return `${config.API_BASE_URL}${config.CFP_SESSIONS_PATH}/${encodedPath}`;
};

export const MAX_CFP_SESSION_DOCUMENT_BYTES = config.MAX_CFP_SESSION_DOCUMENT_SIZE_MB * 1024 * 1024;

export const validateCfpSessionPdfFile = (file) => {
  if (!file) return 'Please select a PDF file.';
  const name = String(file.name ?? '').toLowerCase();
  const type = String(file.type ?? '').toLowerCase();
  if (type !== 'application/pdf' && !name.endsWith('.pdf')) {
    return 'Only PDF files are allowed.';
  }
  if (file.size > MAX_CFP_SESSION_DOCUMENT_BYTES) {
    return `File size must be ${config.MAX_CFP_SESSION_DOCUMENT_SIZE_MB} MB or less.`;
  }
  return null;
};

export const logger = {
  debug: (...args) => {
    if (config.LOGGING.DEBUG_ENABLED) console.log(...args);
  },
  info: (...args) => {
    if (config.LOGGING.CONSOLE_ENABLED) console.log(...args);
  },
  warn: (...args) => {
    if (config.LOGGING.CONSOLE_ENABLED) console.warn(...args);
  },
  error: (...args) => console.error(...args),
};

export default config;